import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import { getKnownAgents } from './callUtils';
import { PhoneIcon, BackspaceIcon } from './Icons';
import SelectMenu from './SelectMenu';

// The agent picker below is a custom SelectMenu (a <button> + popover), not a
// native <select> — .closest('.select-menu') catches its trigger and, while
// open, its option buttons too, so typing/Enter don't leak through to the
// dialpad while it's focused, same as the native select used to block.
const isFormField = (el) =>
  Boolean(el) &&
  (['INPUT', 'SELECT', 'TEXTAREA'].includes(el.tagName) || Boolean(el.closest?.('.select-menu')));

const KEYS = [
  ['1', ''], ['2', 'ABC'], ['3', 'DEF'],
  ['4', 'GHI'], ['5', 'JKL'], ['6', 'MNO'],
  ['7', 'PQRS'], ['8', 'TUV'], ['9', 'WXYZ'],
  ['*', ''], ['0', '+'], ['#', ''],
];

// Standalone dialpad for ringing any number on demand (not just a customer
// already in the call log) - mirrors a mobile phone's dialer, then reuses the
// same click-to-call endpoint IncomingCalls' per-row Call button hits.
function DirectCall({ calls }) {
  const { authFetch, user } = useAuth();
  const knownAgents = getKnownAgents(calls);
  // The logged-in agent's own identity (linked via user.agentNumber) should
  // default the picker to "call as yourself" rather than whichever agent
  // happens to sort first alphabetically among everyone seen in call history.
  const orderedAgents = user?.agentNumber
    ? [
        { number: user.agentNumber, name: user.agentName || user.agentNumber },
        ...knownAgents.filter((agent) => agent.number !== user.agentNumber),
      ]
    : knownAgents;
  const [number, setNumber] = useState('');
  const [agentNumber, setAgentNumber] = useState(orderedAgents[0]?.number || '');
  const [status, setStatus] = useState('idle'); // idle | calling | done | error
  const [errorMessage, setErrorMessage] = useState('');
  const displayRef = useRef(null);

  const pressKey = (key) => {
    setStatus('idle');
    setNumber((prev) => prev + key);
  };

  const backspace = () => {
    setStatus('idle');
    setNumber((prev) => prev.slice(0, -1));
  };

  const handleCall = async () => {
    if (!number || !agentNumber) return;
    setStatus('calling');
    setErrorMessage('');
    try {
      const response = await authFetch('/api/click-to-call', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentNumber, destinationNumber: number }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Call failed');
      setStatus('done');
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.message);
    }
  };

  // Lets the whole page act like a phone dialer: press digit/*/# keys anywhere
  // to punch them in, Backspace to erase, Enter to place the call. Skipped
  // while some other form field (e.g. the agent <select>) has focus so this
  // doesn't hijack normal typing there. The number display input already
  // handles its own typing natively via onChange, so digits/backspace are
  // left alone when it's focused to avoid appending each keystroke twice.
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (status === 'calling') return;
      const active = document.activeElement;
      const isDisplayFocused = active === displayRef.current;
      if (isFormField(active) && !isDisplayFocused) return;

      if (e.key === 'Enter') {
        e.preventDefault();
        handleCall();
        return;
      }
      if (e.key === 'Backspace') {
        if (isDisplayFocused) return;
        e.preventDefault();
        backspace();
        return;
      }
      if (isDisplayFocused) return;

      if (/^[0-9*#]$/.test(e.key)) {
        e.preventDefault();
        pressKey(e.key);
      } else if (e.key === '+') {
        e.preventDefault();
        pressKey('+');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [status, number, agentNumber]);

  return (
    <div className="dialpad-wrap">
      <div className="dialpad-card">
        {orderedAgents.length === 0 ? (
          <p className="agent-page-empty">
            No agents found yet - an agent must answer at least one call before you can place
            outbound calls on their behalf.
          </p>
        ) : (
          <>
            <label className="dialpad-agent-label" htmlFor="dialpad-agent">
              Call as
            </label>
            <SelectMenu
              id="dialpad-agent"
              className="dialpad-agent-select"
              value={agentNumber}
              onChange={(value) => {
                setAgentNumber(value);
                setStatus('idle');
              }}
              disabled={status === 'calling'}
              ariaLabel="Call as"
              options={orderedAgents.map((agent) => ({ value: agent.number, label: agent.name }))}
            />

            <input
              ref={displayRef}
              className="dialpad-display"
              type="tel"
              inputMode="tel"
              placeholder="Enter a number"
              value={number}
              onChange={(e) => {
                setStatus('idle');
                setNumber(e.target.value.replace(/[^\d+*#]/g, ''));
              }}
            />

            <div className="dialpad-grid">
              {KEYS.map(([digit, letters]) => (
                <button
                  key={digit}
                  type="button"
                  className="dialpad-key"
                  onClick={() => pressKey(digit)}
                  disabled={status === 'calling'}
                >
                  <span className="dialpad-key-digit">{digit}</span>
                  {letters && <span className="dialpad-key-letters">{letters}</span>}
                </button>
              ))}
            </div>

            <div className="dialpad-actions">
              <button
                type="button"
                className="dialpad-backspace"
                onClick={backspace}
                disabled={!number || status === 'calling'}
                aria-label="Backspace"
              >
                <BackspaceIcon width={18} height={18} />
              </button>
              <button
                type="button"
                className="dialpad-call-btn"
                onClick={handleCall}
                disabled={!number || status === 'calling'}
              >
                <PhoneIcon width={18} height={18} />
                {status === 'calling' ? 'Calling…' : 'Call'}
              </button>
            </div>

            {status === 'done' && (
              <p className="call-feedback call-feedback-ok dialpad-status">
                Ringing {agentNumber} - it will bridge to {number} once answered.
              </p>
            )}
            {status === 'error' && (
              <p className="call-feedback call-feedback-error dialpad-status">{errorMessage}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default DirectCall;
